import { Component, Input, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { EChartsOption } from 'echarts';
import {
	DashboardsServiceV2,
	WidgetDataDTO,
	WidgetDataSetRequestDTO,
	WidgetDefinitionDTO,
} from 'src/app/core/services/dashboardsv2.service';
import { DashboardWidgetLoadingService } from '../../../dashboard-widget-loading-service';

@Component({
    selector: 'app-stacked-graph',
    templateUrl: './widget-stacked.component.html',
})
export class WidgetStackedComponent implements OnChanges, OnDestroy {
    @Input() widgetConfig: WidgetDefinitionDTO;
    @Input() dataSetRequest: WidgetDataSetRequestDTO;
    @Input() loadWidgetData: boolean;

    echartOptions: EChartsOption;
    dashboardService: DashboardsServiceV2;
    widgetData: WidgetDataDTO;
    subscription: any;
    isLoaded: boolean = false;
    chartInstance: any; // Added chartInstance

    public lastLoadedDataSet: WidgetDataSetRequestDTO | undefined = undefined;

    constructor(
        dashboardService: DashboardsServiceV2,
        private readonly _dashboardWidgetLoadingService: DashboardWidgetLoadingService
    ) {
        this.dashboardService = dashboardService;
    }

    onChartInit(e: any) {
        this.chartInstance = e;
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const hasDataSetChangedSinceLastLoaded =
            JSON.stringify(this.dataSetRequest) !== JSON.stringify(this.lastLoadedDataSet);

        if (changes.dataSetRequest && !changes.dataSetRequest.isFirstChange()) {
            if (this.loadWidgetData && this.widgetConfig.version != 3) {
                this.loadData();
            } else {
                this.markAsLoading();
            }
        }

        if (this.loadWidgetData && hasDataSetChangedSinceLastLoaded && this.widgetConfig.version != 3) {
            this.loadData();
        }

        if (changes.loadWidgetData && !this.isLoaded) {
            if (changes.loadWidgetData.currentValue && this.widgetConfig.version != 3) {
                this.loadData();
            }
            //stop loading unfinished widget
            if (!changes.loadWidgetData.currentValue && this.subscription) {
                this.subscription.unsubscribe();
                this.widgetConfig.loading = false;
                this._dashboardWidgetLoadingService.remove(this.widgetConfig.id.toString());
            }
        }

        // Handle updates for version 3 dashboards where data is pushed directly via widgetConfig.data
        if (changes.widgetConfig && this.widgetConfig.version === 3) {
            if (this.widgetConfig.data) {
                this.widgetData = this.widgetConfig.data;
                this.loadData(); // Call loadData to trigger rendering with existing data
            }
        }
    }

    loadData() {
        this.widgetConfig.loading = true;
        this.markAsLoading();

        if (this.widgetConfig.version === 3 && this.widgetData) {
            // If version 3 and data is already provided, just build the chart
            this.widgetConfig.loading = false;
            this.markAsLoaded();
            this._updateLastLoadedDataSet();

            if (this.widgetData.data) {
                this.getStackedData();
            }
            if (this.chartInstance) {
                this.chartInstance.setOption(this.echartOptions, true);
            }
            return;
        }

        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        this.subscription = this.dashboardService
            .getDataForWidget(this.widgetConfig, this.dataSetRequest)
            .subscribe((response: WidgetDataDTO) => {
                this.widgetData = response;
                this.widgetConfig.loading = false;
                this.markAsLoaded();
                this._updateLastLoadedDataSet();

                if (response.data) {
                    this.getStackedData();
                }
                if (this.chartInstance) {
                    this.chartInstance.setOption(this.echartOptions, true);
                }
            });
    }

    /** Create data for Stacked area graph */
    getStackedData() {
        // get list of xAxis data (months)
        let xAxisLabel = this.widgetData.dimensions.map((a) => a.name);

        let axisOptions = [];
        let dataArray = this.widgetData.data;

        //get data in format
        dataArray.forEach((element, index) => {
            let currLen = dataArray[index].length;
            axisOptions.push({
                type: 'line',
                stack: 'Total',
                emphasis: { focus: 'series' },
                areaStyle: {},
                name: element[0],
                data: element.splice(1, currLen),
            });
        });

        //get the name to display as legends
        let legendData = dataArray.map(function (item) {
            return item[0];
        });

        // set eChart properties
        this.echartOptions = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    label: {
                        backgroundColor: '#6a7985',
                    },
                },
            },
            toolbox: {
                show: true,
                feature: {
                    magicType: { show: true, type: ['line', 'bar'] },
                    dataZoom: { show: false },
                    mark: { show: true },
                    restore: { show: true },
                    saveAsImage: {
                        show: true,
                        name: ('SportsEye_' + this.widgetConfig.title.replace(' ', '_') + '_Export').toLowerCase(),
                    },
                },
            },
            legend: {
                top: 10,
                data: legendData,
            },
            grid: {
                left: '8%',
                right: '8%',
                bottom: '3%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: xAxisLabel,
            },
            yAxis: {
                type: 'value',
                name: this.widgetConfig.title,
                nameLocation: 'middle',
                nameGap: 55,
            },
            series: axisOptions,
        };
        if (this.chartInstance) {
            this.chartInstance.setOption(this.echartOptions, true);
        }
    }

    public markAsLoading(): void {
        this.isLoaded = false;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.add(widgetId);
    }

    public markAsLoaded(): void {
        this.isLoaded = true;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.remove(widgetId);
    }

    private _updateLastLoadedDataSet(): void {
        this.lastLoadedDataSet = { ...this.dataSetRequest };
    }
}
